// libraries
// styles
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useGetVirtualAccounts } from 'src/api/query/creditcards'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import ExpensePolicyUploadBox, {
  type ExpensePolicyUploadBoxGatedState,
} from 'src/components/ExpensePolicyUploadBox/ExpensePolicyUploadBox'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import OrgMemberDetailsBox from 'src/components/OrgMemberDetailsBox/OrgMemberDetailsBox'
import OrgNameAndIndustryBox from 'src/components/OrgNameAndIndustryBox/OrgNameAndIndustryBox'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import OrgVirtualCardsEnablePanel from 'src/feature/OrgVirtualCardsEnablePanel/OrgVirtualCardsEnablePanel'
import { useBackgroundDispatch } from 'src/hooks'
import { useSaasUpgradeAction } from 'src/hooks/useSaasUpgradeAction'
import { useTranslate } from 'src/localization/Trns'
import { useQueryOrganizationSaasTier } from 'src/queries/organizations/saas.queries'
import { getOrganization } from 'src/redux/actions/data/organizations.actions'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectOrganizationIdFromRoute,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { isExpensePolicyAIEnabled } from 'src/utils/featureFlags'

import styles from './OrganizationInfo.style'

const OrganizationInfo: React.FC = () => {
  const user = useCurrentUser()
  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const appName = useSelector(selectAppName)
  const upgradeSaasSubscription = useSaasUpgradeAction(organization?.id)

  const featureFlags = useSelector(selectFeatureFlaggerFromState)
  const showExpensePolicyAI = isExpensePolicyAIEnabled(
    featureFlags,
    user?.organizationId,
  )

  const translate = useTranslate()

  const dispatch = useBackgroundDispatch()
  useEffect(() => {
    if (organizationId) {
      dispatch(getOrganization(organizationId))
    }
    // TODO - should not assume user exists in redux, so should load user here if iti doesn't exist
    // dispatch(getUser)
  }, [organizationId])

  const {
    data: organizationSaasTier,
    isFetched: isOrganizationSaasTierFetched,
  } = useQueryOrganizationSaasTier({
    orgId: organization?.id || '',
    enabled: !!organization?.id,
  })

  const { data: virtualAccountsData } = useGetVirtualAccounts(undefined, {
    enabled: !!organization?.id,
  })
  const virtualAccounts = virtualAccountsData?.virtualAccounts ?? []
  // TODO: Multiple virtual accounts not yet supported - using first account
  const virtualAccount =
    virtualAccounts.length > 0 ? virtualAccounts[0] : undefined

  if (user === undefined || organization === undefined) {
    return <LoadingBackgroundPage />
  }

  const isSaasBillingExcluded =
    isOrganizationSaasTierFetched && organizationSaasTier === undefined
  const canUseExpensePolicyAgent =
    isSaasBillingExcluded ||
    (organizationSaasTier !== undefined &&
      organizationSaasTier.tier !== 'STARTER')
  const expensePolicyGatedState: ExpensePolicyUploadBoxGatedState | undefined =
    isOrganizationSaasTierFetched && !canUseExpensePolicyAgent
      ? { type: 'not_entitled' }
      : undefined
  const canRenderExpensePolicyBox =
    showExpensePolicyAI && isOrganizationSaasTierFetched

  return (
    <>
      <Helmet>
        <title>{`Organization Info | ${appName}`}</title>
        <meta
          name="description"
          content={`Organization Info page of ${appName}`}
        />
      </Helmet>
      <LayoutShell
        subnavOptions={[]}
        subnavDesign={SubnavDesign.Floating}
        content={
          <BackgroundPageContent
            className="OrganizationInfo"
            css={styles}
            noGap
          >
            <OrgNameAndIndustryBox user={user} organization={organization} />
            <OrgMemberDetailsBox user={user} organization={organization} />
            {virtualAccount && (
              <OrgVirtualCardsEnablePanel virtualAccount={virtualAccount} />
            )}
            {canRenderExpensePolicyBox && (
              <ExpensePolicyUploadBox
                organization={organization}
                user={user}
                gatedState={expensePolicyGatedState}
                onUpgradeToPro={() =>
                  upgradeSaasSubscription('PRO', 'Expense Policy Agent')
                }
              />
            )}
          </BackgroundPageContent>
        }
        title={<span>{translate('Organization Info')}</span>}
        buttons={[]}
      />
    </>
  )
}

export default OrganizationInfo
